import { useState, useEffect } from 'react';
import { Instagram, Link2, Link2Off, Image, Loader, AlertCircle, ExternalLink } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InstagramConnect = ({ onPostsLoaded }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState({ connected: false });
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [postsLoading, setPostsLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [noAppId, setNoAppId] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Check connected status
    fetch(`${API_URL}/api/instagram/status`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : {})
      .then(d => setStatus(d))
      .catch(() => {});

    // Check if returning from OAuth
    if (window.location.hash?.includes('instagram_connected=1')) {
      setStatus({ connected: true });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [user]);

  const handleConnect = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/instagram/auth-url`, { credentials: 'include' });
      if (!res.ok || res.status >= 500) { setNoAppId(true); setLoading(false); return; }
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch { setLoading(false); }
  };

  const handleDisconnect = async () => {
    await fetch(`${API_URL}/api/instagram/disconnect`, { method: 'DELETE', credentials: 'include' });
    setStatus({ connected: false });
    setPosts([]);
  };

  const loadPosts = async () => {
    setPostsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/instagram/posts?limit=12`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts || []);
        onPostsLoaded?.(data.posts || []);
      }
    } catch { /* silent */ } finally { setPostsLoading(false); }
  };

  if (!user) return null;

  return (
    <Card className={`border ${status.connected ? 'border-[#E1306C]/20 bg-[#FFF5F9]' : 'border-gray-200'}`}>
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-[#E1306C] via-[#FD1D1D] to-[#F77737] flex items-center justify-center flex-shrink-0">
            <Instagram className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-gray-900 text-sm">Instagram</h3>
              {status.connected && <Badge className="bg-[#E1306C] text-white text-[10px]">Connected</Badge>}
            </div>
            {status.connected
              ? <p className="text-xs text-gray-500">@{status.ig_name || 'your account'} linked</p>
              : <p className="text-xs text-gray-500">Import your food posts to FoodQuest</p>}
          </div>
          {status.connected ? (
            <Button variant="ghost" size="sm" onClick={handleDisconnect} className="text-red-400 hover:text-red-600 text-xs gap-1">
              <Link2Off className="w-3 h-3" />Disconnect
            </Button>
          ) : (
            <Button
              onClick={handleConnect}
              disabled={loading}
              size="sm"
              className="bg-gradient-to-r from-[#E1306C] to-[#F77737] text-white text-xs gap-1.5 rounded-2xl"
            >
              {loading ? <Loader className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}
              Connect
            </Button>
          )}
        </div>

        {/* No App ID configured */}
        {noAppId && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-amber-800">Instagram App ID required</p>
              <p className="text-xs text-amber-700 mt-0.5">
                To enable Instagram, create a Meta Developer App at{' '}
                <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-0.5">
                  developers.facebook.com <ExternalLink className="w-3 h-3" />
                </a>{' '}and add <code className="bg-amber-100 px-1 rounded">INSTAGRAM_APP_ID</code> + <code className="bg-amber-100 px-1 rounded">INSTAGRAM_APP_SECRET</code> to your backend .env
              </p>
            </div>
          </div>
        )}

        {/* Load posts button */}
        {status.connected && (
          <div className="mt-4">
            <button onClick={() => { setExpanded(v => !v); if (!expanded && posts.length === 0) loadPosts(); }}
              className="text-xs text-[#E1306C] font-medium flex items-center gap-1.5 hover:underline">
              <Image className="w-3.5 h-3.5" />
              {expanded ? 'Hide' : 'Show'} my Instagram posts
            </button>

            {expanded && (
              <div className="mt-3">
                {postsLoading ? (
                  <div className="flex items-center gap-2 py-4 text-gray-400 text-sm">
                    <Loader className="w-4 h-4 animate-spin" />Loading posts...
                  </div>
                ) : posts.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {posts.map(p => (
                      <div key={p.id} className="aspect-square rounded-2xl overflow-hidden bg-gray-100">
                        <img loading="lazy" src={p.image} alt={p.caption} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 py-3">No image posts found on your Instagram.</p>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InstagramConnect;
